/**
 * AudioQueueState — tracks the pending TTS audio FIFO and whether
 * an audio element is currently playing. Owned by TtsPlayer service
 * but exposed as a model so views can show "Speaking (+N queued)".
 *
 * Invariants from the original audio-manager design:
 *   - The currently-playing item is NEVER interrupted
 *   - Pending queue is capped at AUDIO_QUEUE_MAX; if a new item
 *     arrives while full, the OLDEST pending is dropped (GPS context
 *     is time-sensitive, stale replies are less valuable than fresh)
 *
 * Emits `audio:queue-changed` on any mutation.
 */
export class AudioQueueState {
    constructor(_bus) {
        this._bus = _bus;
        this._queue = [];
        this._ttsPlaying = false;
    }
    /** Enqueue a new audio item, dropping oldest if at capacity */
    enqueue(item) {
        let dropped = null;
        while (this._queue.length >= AudioQueueState.MAX_PENDING) {
            dropped = this._queue.shift() ?? null;
        }
        this._queue.push(item);
        this._emit();
        return { dropped };
    }
    /** Remove and return the next pending item */
    dequeue() {
        const item = this._queue.shift() ?? null;
        this._emit();
        return item;
    }
    /** Clear all pending items without touching the currently-playing one */
    clearPending() {
        const dropped = this._queue.slice();
        this._queue = [];
        this._emit();
        return dropped;
    }
    setPlaying(playing) {
        this._ttsPlaying = playing;
        this._emit();
    }
    get pendingCount() {
        return this._queue.length;
    }
    get isPlaying() {
        return this._ttsPlaying;
    }
    get isIdle() {
        return !this._ttsPlaying && this._queue.length === 0;
    }
    _emit() {
        this._bus.emit('audio:queue-changed', {
            pendingCount: this._queue.length,
            isPlaying: this._ttsPlaying,
        });
    }
}
/** Max pending (not counting currently playing) */
AudioQueueState.MAX_PENDING = 1;
//# sourceMappingURL=AudioQueueState.js.map